import { nanoid } from 'nanoid';

// All 48 personality traits with their metadata
const PERSONALITY_TRAITS = [
  { id: 'empathetic', emoji: '❤️', name: 'Empathetic', rarity: 'common' },
  { id: 'analytical', emoji: '🧠', name: 'Analytical', rarity: 'uncommon' },
  { id: 'creative', emoji: '🎨', name: 'Creative', rarity: 'common' },
  { id: 'leader', emoji: '👑', name: 'Natural Leader', rarity: 'rare' },
  { id: 'intuitive', emoji: '🔮', name: 'Intuitive', rarity: 'uncommon' },
  { id: 'strategic', emoji: '♟️', name: 'Strategic', rarity: 'uncommon' },
  { id: 'passionate', emoji: '🔥', name: 'Passionate', rarity: 'common' },
  { id: 'resilient', emoji: '💪', name: 'Resilient', rarity: 'uncommon' },
  { id: 'visionary', emoji: '🚀', name: 'Visionary', rarity: 'rare' },
  { id: 'diplomatic', emoji: '🤝', name: 'Diplomatic', rarity: 'uncommon' },
  { id: 'innovative', emoji: '💡', name: 'Innovative', rarity: 'rare' },
  { id: 'charismatic', emoji: '✨', name: 'Charismatic', rarity: 'rare' },
  { id: 'adaptable', emoji: '🦎', name: 'Adaptable', rarity: 'common' },
  { id: 'authentic', emoji: '💎', name: 'Authentic', rarity: 'uncommon' },
  { id: 'ambitious', emoji: '🎯', name: 'Ambitious', rarity: 'common' },
  { id: 'mindful', emoji: '🧘', name: 'Mindful', rarity: 'uncommon' },
  { id: 'genius', emoji: '🌟', name: 'Hidden Genius', rarity: 'legendary' },
  { id: 'empath', emoji: '🫂', name: 'Empath', rarity: 'rare' },
  { id: 'maverick', emoji: '🦅', name: 'Maverick', rarity: 'rare' },
  { id: 'sage', emoji: '📚', name: 'Wise Sage', rarity: 'legendary' },
  { id: 'explorer', emoji: '🗺️', name: 'Explorer', rarity: 'common' },
  { id: 'protector', emoji: '🛡️', name: 'Protector', rarity: 'uncommon' },
  { id: 'healer', emoji: '🌿', name: 'Healer', rarity: 'rare' },
  { id: 'pioneer', emoji: '🏔️', name: 'Pioneer', rarity: 'rare' },
  { id: 'oracle', emoji: '🔯', name: 'Oracle', rarity: 'legendary' },
  { id: 'warrior', emoji: '⚔️', name: 'Warrior Spirit', rarity: 'uncommon' },
  { id: 'dreamer', emoji: '☁️', name: 'Dreamer', rarity: 'common' },
  { id: 'architect', emoji: '🏗️', name: 'Architect', rarity: 'uncommon' },
  { id: 'phoenix', emoji: '🔥', name: 'Phoenix', rarity: 'legendary' },
  { id: 'catalyst', emoji: '⚡', name: 'Catalyst', rarity: 'rare' },
  { id: 'harmonizer', emoji: '🎵', name: 'Harmonizer', rarity: 'uncommon' },
  { id: 'alchemist', emoji: '⚗️', name: 'Alchemist', rarity: 'legendary' },
  { id: 'guardian', emoji: '🌙', name: 'Guardian', rarity: 'uncommon' },
  { id: 'mystic', emoji: '🌌', name: 'Mystic', rarity: 'legendary' },
  { id: 'rebel', emoji: '🔥', name: 'Rebel', rarity: 'uncommon' },
  { id: 'mentor', emoji: '🎓', name: 'Mentor', rarity: 'rare' },
  { id: 'artist', emoji: '🖼️', name: 'Artist Soul', rarity: 'uncommon' },
  { id: 'philosopher', emoji: '💭', name: 'Philosopher', rarity: 'rare' },
  { id: 'lightbringer', emoji: '☀️', name: 'Lightbringer', rarity: 'legendary' },
  { id: 'shapeshifter', emoji: '🎭', name: 'Shapeshifter', rarity: 'rare' },
  { id: 'timekeeper', emoji: '⏳', name: 'Timekeeper', rarity: 'rare' },
  { id: 'truthseeker', emoji: '🔍', name: 'Truth Seeker', rarity: 'uncommon' },
  { id: 'cosmic', emoji: '🌠', name: 'Cosmic Mind', rarity: 'legendary' },
  { id: 'elementalist', emoji: '🌊', name: 'Elementalist', rarity: 'rare' },
  { id: 'navigator', emoji: '🧭', name: 'Navigator', rarity: 'uncommon' },
  { id: 'quantum', emoji: '⚛️', name: 'Quantum Thinker', rarity: 'legendary' },
  { id: 'enchanter', emoji: '✨', name: 'Enchanter', rarity: 'rare' },
  { id: 'sovereign', emoji: '👸', name: 'Sovereign', rarity: 'legendary' }
];

/**
 * UserDataService - Coherent, compact user data model for Cloudflare KV
 * Focused on monetization: trait unlocks, email capture, premium upgrades
 */
class UserDataService {
  constructor() {
    this.LOCAL_KEY = 'pq_user_v2'; // v2 for new model
    this.initialized = false;
  }

  /**
   * Initialize or load user profile
   */
  initialize() {
    if (this.initialized) return this.getProfile();
    
    let profile = this.getLocalProfile();
    
    if (!profile) {
      profile = this.createNewProfile();
      this.saveLocal(profile);
    }
    
    this.initialized = true;
    return profile;
  }

  /**
   * Create a new user profile - COMPACT and COHERENT
   */
  createNewProfile() {
    const now = Date.now();
    const personalityCode = this.generatePersonalityDNA();
    
    return {
      // Core identity
      id: `anon_${nanoid(12)}`,
      created: now,
      lastActive: now,
      
      // Personality DNA - generated once, never changes
      personalityDNA: {
        code: personalityCode.code,
        rarity: personalityCode.rarity,
        generatedAt: now
      },
      
      // Trait progression - the main game loop
      traits: {
        unlocked: ['empathetic', 'creative', 'passionate'], // Start with 3 teaser traits
        unlockedAt: {
          'empathetic': now,
          'creative': now,
          'passionate': now
        }
      },
      
      // Quiz history - drives trait unlocks
      quizHistory: [],
      
      // Monetization tracking
      monetization: {
        email: null,
        emailCapturedAt: null,
        premium: false,
        premiumPurchasedAt: null,
        paymentId: null
      },
      
      // Gamification
      stats: {
        level: 1,
        xp: 0,
        streak: 0,
        accuracy: 0,
        totalQuizzes: 0,
        lastQuizDate: null
      },
      
      // Achievements for engagement
      achievements: [],
      
      // Daily mechanics
      daily: {
        lastSpin: null,
        spinsRemaining: 1
      },
      
      // Auth (for Cloudflare sync)
      auth: {
        type: 'anon',
        identifier: null,
        verified: false
      }
    };
  }

  /**
   * Generate unique personality DNA code
   */
  generatePersonalityDNA() {
    const types = ['INTJ', 'ENFP', 'ISTP', 'ESFJ', 'ENTP', 'ISFJ', 'INFP', 'ESTJ', 
                   'INFJ', 'ESTP', 'ISFP', 'ENTJ', 'INTP', 'ESFP', 'ISTJ', 'ENFJ'];
    const animals = ['Dragon', 'Phoenix', 'Wolf', 'Eagle', 'Lion', 'Owl', 'Dolphin', 
                     'Tiger', 'Fox', 'Bear', 'Raven', 'Serpent', 'Hawk', 'Panther'];
    
    const type = types[Math.floor(Math.random() * types.length)];
    const animal = animals[Math.floor(Math.random() * animals.length)];
    const rarity = Math.floor(Math.random() * 9000) + 1000; // 1 in 1000-10000
    
    return {
      code: `${type}-${animal}`,
      rarity
    };
  }

  /**
   * Get profile from localStorage
   */
  getLocalProfile() {
    try {
      const stored = localStorage.getItem(this.LOCAL_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Failed to load profile:', e);
      return null;
    }
  }

  /**
   * Get current profile
   */
  getProfile() {
    return this.getLocalProfile() || this.createNewProfile();
  }

  /**
   * Save profile to localStorage
   */
  saveLocal(profile) {
    try {
      profile.lastActive = Date.now();
      localStorage.setItem(this.LOCAL_KEY, JSON.stringify(profile));
      return true;
    } catch (e) {
      console.error('Failed to save profile:', e);
      return false;
    }
  }

  /**
   * Complete a quiz and unlock traits
   */
  addQuizResult(quizId, score, result, category) {
    const profile = this.getProfile();
    
    // Record quiz
    const quizRecord = {
      id: quizId,
      score,
      result,
      category,
      completedAt: Date.now(),
      unlockedTraits: []
    };
    
    // Unlock 2-3 traits based on score
    const traitsToUnlock = this.selectTraitsToUnlock(profile, score, category);
    quizRecord.unlockedTraits = traitsToUnlock;
    
    // Update profile
    traitsToUnlock.forEach(traitId => {
      if (!profile.traits.unlocked.includes(traitId)) {
        profile.traits.unlocked.push(traitId);
        profile.traits.unlockedAt[traitId] = Date.now();
      }
    });
    
    // Add to history (keep last 20)
    profile.quizHistory.unshift(quizRecord);
    profile.quizHistory = profile.quizHistory.slice(0, 20);
    
    // Update stats
    profile.stats.totalQuizzes++;
    profile.stats.xp += Math.round(score * 10);
    profile.stats.level = Math.floor(profile.stats.xp / 1000) + 1;
    
    // Update accuracy
    const totalScore = profile.quizHistory.reduce((sum, q) => sum + q.score, 0);
    profile.stats.accuracy = Math.round(totalScore / profile.quizHistory.length);
    
    // Update streak
    this.updateStreak(profile);
    
    // Check achievements
    this.checkAchievements(profile);
    
    // Save
    this.saveLocal(profile);
    
    return {
      profile,
      newTraits: traitsToUnlock,
      levelUp: profile.stats.xp >= (profile.stats.level * 1000)
    };
  }

  /**
   * Select which traits to unlock based on quiz
   */
  selectTraitsToUnlock(profile, score, category) {
    const numToUnlock = score >= 90 ? 3 : score >= 70 ? 2 : 1;
    const locked = PERSONALITY_TRAITS.filter(t => !profile.traits.unlocked.includes(t.id));
    
    if (locked.length === 0) return [];
    
    // Weighted selection based on rarity
    const weights = {
      common: 40,
      uncommon: 30,
      rare: 20,
      legendary: 10
    };
    
    const selected = [];
    for (let i = 0; i < Math.min(numToUnlock, locked.length); i++) {
      const trait = this.weightedRandomSelect(locked.filter(t => !selected.includes(t.id)), weights);
      if (trait) selected.push(trait.id);
    }
    
    return selected;
  }

  /**
   * Weighted random selection
   */
  weightedRandomSelect(items, weights) {
    const weighted = [];
    items.forEach(item => {
      const weight = weights[item.rarity] || 10;
      for (let i = 0; i < weight; i++) {
        weighted.push(item);
      }
    });
    return weighted[Math.floor(Math.random() * weighted.length)];
  }

  /**
   * Update daily streak
   */
  updateStreak(profile) {
    const now = new Date();
    const lastQuiz = profile.stats.lastQuizDate ? new Date(profile.stats.lastQuizDate) : null;
    
    if (!lastQuiz) {
      profile.stats.streak = 1;
    } else {
      const daysDiff = Math.floor((now - lastQuiz) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        // Same day
      } else if (daysDiff === 1) {
        profile.stats.streak++;
      } else {
        profile.stats.streak = 1;
      }
    }
    
    profile.stats.lastQuizDate = now.toISOString();
  }

  /**
   * Check and award achievements
   */
  checkAchievements(profile) {
    const newAchievements = [];
    
    // Speed demon - first quiz
    if (profile.stats.totalQuizzes === 1 && !profile.achievements.includes('first-quiz')) {
      profile.achievements.push('first-quiz');
      newAchievements.push('first-quiz');
    }
    
    // Accuracy achievements
    if (profile.stats.accuracy >= 90 && !profile.achievements.includes('perfectionist')) {
      profile.achievements.push('perfectionist');
      newAchievements.push('perfectionist');
    }
    
    // Streak achievements
    if (profile.stats.streak >= 3 && !profile.achievements.includes('streak-warrior')) {
      profile.achievements.push('streak-warrior');
      newAchievements.push('streak-warrior');
    }
    
    // Trait collector
    const unlockedCount = profile.traits.unlocked.length;
    if (unlockedCount >= 10 && !profile.achievements.includes('trait-collector')) {
      profile.achievements.push('trait-collector');
      newAchievements.push('trait-collector');
    }
    
    if (unlockedCount >= 24 && !profile.achievements.includes('half-way')) {
      profile.achievements.push('half-way');
      newAchievements.push('half-way');
    }
    
    if (unlockedCount >= 48 && !profile.achievements.includes('complete-personality')) {
      profile.achievements.push('complete-personality');
      newAchievements.push('complete-personality');
    }
    
    // Speed achievements
    if (!profile.achievements.includes('speed-demon')) {
      profile.achievements.push('speed-demon');
      newAchievements.push('speed-demon');
    }
    
    return newAchievements;
  }

  /**
   * Capture email (for saving personality)
   */
  async captureEmail(email) {
    const profile = this.getProfile();
    
    try {
      // Call Cloudflare API to save email
      const response = await fetch('https://personalityquiz-api.jajmangold.workers.dev/api/user/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          userId: profile.id,
          personalityDNA: profile.personalityDNA
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        if (result.code === 'EMAIL_EXISTS') {
          throw new Error('This email is already registered');
        }
        throw new Error(result.error || 'Failed to save email');
      }
      
      // Update local profile
      profile.monetization.email = email;
      profile.monetization.emailCapturedAt = Date.now();
      profile.auth = {
        type: 'email',
        identifier: email,
        verified: false
      };
      
      this.saveLocal(profile);
      
      // Sync full profile to Cloudflare
      await this.syncToCloudflare(profile);
      
      return profile;
    } catch (error) {
      console.error('Email capture error:', error);
      throw error;
    }
  }

  /**
   * Upgrade to premium
   */
  async upgradeToPremium(paymentId) {
    const profile = this.getProfile();
    
    // Unlock ALL traits instantly
    PERSONALITY_TRAITS.forEach(trait => {
      if (!profile.traits.unlocked.includes(trait.id)) {
        profile.traits.unlocked.push(trait.id);
        profile.traits.unlockedAt[trait.id] = Date.now();
      }
    });
    
    // Mark as premium
    profile.monetization.premium = true;
    profile.monetization.premiumPurchasedAt = Date.now();
    profile.monetization.paymentId = paymentId;
    
    // Award achievement
    if (!profile.achievements.includes('premium-member')) {
      profile.achievements.push('premium-member');
    }
    
    this.saveLocal(profile);
    
    // Sync to Cloudflare
    await this.syncToCloudflare(profile);
    
    return profile;
  }

  /**
   * Daily spin wheel - unlock 1 random trait
   */
  dailySpin() {
    const profile = this.getProfile();
    const now = Date.now();
    const lastSpin = profile.daily.lastSpin;
    
    // Check if can spin
    if (lastSpin) {
      const hoursSinceLastSpin = (now - lastSpin) / (1000 * 60 * 60);
      if (hoursSinceLastSpin < 24) {
        return {
          canSpin: false,
          hoursRemaining: Math.ceil(24 - hoursSinceLastSpin)
        };
      }
    }
    
    // Perform spin
    const locked = PERSONALITY_TRAITS.filter(t => !profile.traits.unlocked.includes(t.id));
    if (locked.length === 0) {
      return {
        canSpin: false,
        allUnlocked: true
      };
    }
    
    // Random trait unlock
    const trait = locked[Math.floor(Math.random() * locked.length)];
    profile.traits.unlocked.push(trait.id);
    profile.traits.unlockedAt[trait.id] = now;
    profile.daily.lastSpin = now;
    
    // Bonus XP
    profile.stats.xp += 50;
    
    this.saveLocal(profile);
    
    return {
      canSpin: true,
      unlockedTrait: trait,
      profile
    };
  }

  /**
   * Share for unlock - unlock 3 random traits
   */
  shareForUnlock() {
    const profile = this.getProfile();
    const locked = PERSONALITY_TRAITS.filter(t => !profile.traits.unlocked.includes(t.id));
    
    if (locked.length === 0) return { success: false, allUnlocked: true };
    
    // Unlock 3 random traits
    const toUnlock = [];
    for (let i = 0; i < Math.min(3, locked.length); i++) {
      const trait = locked[Math.floor(Math.random() * locked.length)];
      if (!toUnlock.includes(trait) && !profile.traits.unlocked.includes(trait.id)) {
        toUnlock.push(trait);
        profile.traits.unlocked.push(trait.id);
        profile.traits.unlockedAt[trait.id] = Date.now();
      }
    }
    
    this.saveLocal(profile);
    
    return {
      success: true,
      unlockedTraits: toUnlock,
      profile
    };
  }

  /**
   * Sync to Cloudflare KV (when email is captured)
   */
  async syncToCloudflare(profile) {
    if (!profile.auth?.identifier) return;
    
    try {
      const response = await fetch('https://personalityquiz-api.jajmangold.workers.dev/api/user/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.compressForCloudflare(profile))
      });
      
      return response.ok;
    } catch (e) {
      console.error('Cloudflare sync failed:', e);
      return false;
    }
  }

  /**
   * Compress profile for Cloudflare KV storage
   */
  compressForCloudflare(profile) {
    return {
      id: profile.id,
      dna: profile.personalityDNA.code,
      rarity: profile.personalityDNA.rarity,
      traits: profile.traits.unlocked.join(','),
      email: profile.monetization.email,
      premium: profile.monetization.premium ? 1 : 0,
      xp: profile.stats.xp,
      created: profile.created
    };
  }

  /**
   * Get level progress percentage
   */
  getLevelProgress() {
    const profile = this.getProfile();
    const currentLevelXP = (profile.stats.level - 1) * 1000;
    const progress = profile.stats.xp - currentLevelXP;
    return Math.round((progress / 1000) * 100);
  }

  /**
   * Get brain map percentage (for compatibility)
   */
  getBrainMapPercentage() {
    const profile = this.getProfile();
    return Math.round((profile.traits.unlocked.length / PERSONALITY_TRAITS.length) * 100);
  }
  
  /**
   * Get all personality traits (for UI)
   */
  getAllTraits() {
    return PERSONALITY_TRAITS;
  }
}

// Export singleton
const userDataService = new UserDataService();
export default userDataService;